/**
 * Converts a timestamp string in mm:ss or mm:ss.ms format to seconds
 * @param timestamp - The timestamp string in mm:ss or mm:ss.ms format (e.g., "02:30" or "02:30.500")
 * @returns The total number of seconds (as a decimal if milliseconds are present)
 * @throws Error if the format is invalid
 */
export function timestampToSeconds(timestamp: string): number {
  const parts = timestamp.split(':');

  if (parts.length !== 2) {
    throw new Error(`Invalid timestamp format: ${timestamp}. Expected format: mm:ss or mm:ss.ms`);
  }

  const minutes = parseInt(parts[0], 10);

  // Check if seconds part contains milliseconds
  let seconds: number;
  let milliseconds = 0;

  if (parts[1].includes('.')) {
    const secondsParts = parts[1].split('.');
    seconds = parseInt(secondsParts[0], 10);
    milliseconds = parseInt(secondsParts[1], 10);

    if (isNaN(milliseconds)) {
      throw new Error(`Invalid timestamp format: ${timestamp}. Milliseconds must be a number`);
    }
  } else {
    seconds = parseInt(parts[1], 10);
  }

  if (isNaN(minutes) || isNaN(seconds)) {
    throw new Error(`Invalid timestamp format: ${timestamp}. Minutes and seconds must be numbers`);
  }

  if (seconds < 0 || seconds >= 60) {
    throw new Error(`Invalid seconds value: ${seconds}. Seconds must be between 0 and 59`);
  }

  if (minutes < 0) {
    throw new Error(`Invalid minutes value: ${minutes}. Minutes must be positive`);
  }

  // Convert milliseconds to fractional seconds
  const fractionalSeconds = milliseconds / 1000;

  return minutes * 60 + seconds + fractionalSeconds;
}